import { createGame } from "../core/world";
import { euro, need } from "./format";
import type { Hud } from "./hud";
import type { UiState } from "./state";
import {
  activateSlot, createReplaySlot, deleteSlot, exportSlot, importSlotFile, listSlots,
  newSeed, readSave, renameSlot, saveError,
} from "./storage";

/**
 * The save menu. A <dialog> over the storage functions — focus trapping and
 * Escape come from the platform.
 *
 * The list is rebuilt every time the dialog opens, never per frame, and its
 * buttons are handled by one delegated listener. That is what keeps this clear
 * of the trap Panel documents: rebuilt markup with dead click handlers.
 *
 * Returns the function that opens it, so that a fresh world can be named the
 * moment it is created (§3.6 of RFC-0002).
 */

export function bindSaveMenu(
  persistCurrent: () => void, ui: UiState, hud: Hud,
): (focusSlotId?: string) => void {
  const dialog = need<HTMLDialogElement>("saves");
  const list = need("saves-list");
  const error = need("saves-error");
  const file = need<HTMLInputElement>("save-file");

  /** Switching worlds saves the current one first, so nothing is lost. */
  const leaveCurrent = (): void => persistCurrent();

  function render(): void {
    const active = readSave().activeSlot;
    const slots = listSlots();

    list.innerHTML =
      slots.length === 0
        ? `<p class="hint-body">Noch keine gespeicherte Welt.</p>`
        : slots
            .map((s) => {
              const w = s.world;
              return `
                <div class="save-row" data-id="${s.id}" data-active="${s.id === active}">
                  <input class="save-name" value="${escapeAttr(s.name)}"
                         aria-label="Name der Welt ${escapeAttr(s.name)}" />
                  <span class="save-meta">Tag ${w.day} ·
                        ${w.routes.length} ${w.routes.length === 1 ? "Strecke" : "Strecken"} ·
                        ${w.trucks.length} Lkw · ${euro(w.money)} ·
                        <span class="save-seed">Seed ${w.seed}</span></span>
                  <div class="btn-group">
                    <button data-act="load" ${s.id === active ? "disabled" : ""}
                            aria-label="Welt ${escapeAttr(s.name)} laden">Laden</button>
                    <button data-act="export"
                            aria-label="Welt ${escapeAttr(s.name)} exportieren">Export</button>
                    <button data-act="delete"
                            aria-label="Welt ${escapeAttr(s.name)} löschen">Löschen</button>
                  </div>
                </div>`;
            })
            .join("");

    const problem = saveError();
    error.textContent = problem ?? "";
    error.hidden = problem === null;
  }

  list.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>("button[data-act]");
    const row = btn?.closest<HTMLElement>(".save-row");
    const id = row?.dataset["id"];
    if (!btn || !id) return;

    if (btn.dataset["act"] === "export") {
      exportSlot(id);
      return;
    }
    if (btn.dataset["act"] === "load") {
      leaveCurrent();
      activateSlot(id);
      location.reload(); // renderer, panel and selection all hold the old state
      return;
    }
    // Deleting is irreversible and there is no undo. Name the world in the prompt.
    const name = row?.querySelector<HTMLInputElement>(".save-name")?.value ?? "";
    if (!confirm(`„${name}“ endgültig löschen?`)) return;
    deleteSlot(id);
    render();
  });

  // Rename on blur and on Enter, so both a click elsewhere and the keyboard work.
  list.addEventListener("focusout", (ev) => {
    const input = ev.target as HTMLElement;
    if (!input.classList.contains("save-name")) return;
    const id = input.closest<HTMLElement>(".save-row")?.dataset["id"];
    if (id) {
      renameSlot(id, (input as HTMLInputElement).value);
      render();
    }
  });
  list.addEventListener("keydown", (ev) => {
    if ((ev as KeyboardEvent).key === "Enter") (ev.target as HTMLElement).blur();
  });

  // The game pauses while the menu is open. Otherwise the rows go stale behind
  // the dialog — and nobody is playing while they are picking a world anyway.
  let resumeSpeed = 1;

  /**
   * With a slot id, the dialog opens on that row with its name selected, so the
   * first keystroke replaces the suggestion instead of appending to it.
   */
  function open(focusSlotId?: string): void {
    resumeSpeed = ui.speed;
    hud.setSpeed(0);
    render();
    dialog.showModal();

    if (focusSlotId === undefined) return;
    const name = list.querySelector<HTMLInputElement>(
      `.save-row[data-id="${focusSlotId}"] .save-name`,
    );
    name?.focus();
    name?.select();
  }

  need("saves-open").addEventListener("click", () => open());
  need("saves-close").addEventListener("click", () => dialog.close());
  dialog.addEventListener("close", () => hud.setSpeed(resumeSpeed)); // fires on Escape too

  need("saves-new").addEventListener("click", () => {
    leaveCurrent();
    createReplaySlot(createGame(newSeed()));
    location.reload();
  });

  need("saves-import").addEventListener("click", () => file.click());
  file.addEventListener("change", async () => {
    const picked = file.files?.[0];
    file.value = ""; // sonst löst dieselbe Datei kein zweites change aus
    if (!picked) return;
    leaveCurrent();
    if (await importSlotFile(picked)) location.reload();
    else alert("Das ist kein gültiger UMSCHLAG-Spielstand.");
  });

  return open;
}

/** Slot names are player input and land in an attribute. Exported for the test. */
export function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}
